import _ from 'lodash';
import Cookie from 'js-cookie';
import { backOffice } from '@ubeeqo/web-api-client';
import { Company } from '@/store/object-values/Company';
import { PROFILES, NOTIFICATIONS } from '@/constants';
import * as CompanySettingsModule from '@/domains/UserCompany/Company/Admin/CompanySettings/CompanySettingsModule';
import { Auth } from '@/main';

export const GETTERS = {
  GET_IS_COMPANY_ADMIN: 'isCompanyAdmin',
};

export const MUTATIONS = {
  SET_STATUS: 'setStatus',
  SET_SELECTED_PROFILE: 'setSelectedProfile',
  SET_INITIAL_SELECTED_PROFILE: 'setInitialSelectedProfile',
  SET_ALL_LOCATION: 'setCostAllocations',
  SET_COMPANY: 'setCompany',
  SET_USER_ACCOUNT: 'setUserAccount',
  CLEAR_PASSWORD: 'clearPassword',
};

export const NAMESPACE = 'UserAccount';
export const FULL_NAMESPACE = `App/${NAMESPACE}`;

const DEFAULT_DATA = {
  isCompanyAdmin: false,
  permissions: '',
  picture: '',
  name: '',
  uuid: '',
  firstName: '',
  lastName: '',
  phone: '',
  company: {},
  password: {
    passwordStep1: '',
    passwordStep2: '',
  },
  defaultCityUuid: '',
  profiles: [],
};

const DEFAULT_STATE = {
  IDLE: true,
  LOADING: false,
  LOADED: false,
  ERROR: false,
};

export default {
  namespaced: true,
  state: {
    data: { ...DEFAULT_DATA },
    STATUS: { ...DEFAULT_STATE },
    selectedProfile: undefined,
  },
  getters: {
    [GETTERS.GET_IS_COMPANY_ADMIN](state) {
      return state.data.isCompanyAdmin;
    },
    businessProfile({ data }) {
      return _.get(data, 'profiles', []).find(item => item.alias === PROFILES.BUSINESS);
    },
    hasBusinnesProfile({ data }) {
      return _.get(data, 'profiles', []).some(item => item.alias === PROFILES.BUSINESS);
    },
  },
  mutations: {
    setStatus(state, value) {
      const states = Object.keys(state.STATUS);
      if (states.indexOf(value) > -1) {
        states.forEach((key) => {
          state.STATUS[key] = (key === value);
        });
      }
    },
    setSelectedProfile(state, value) {
      state.selectedProfile = value;
    },
    setInitialSelectedProfile(state) {
      const businessProfile = state.data.profiles.find(item => item.alias === PROFILES.BUSINESS);
      const initialProfile = businessProfile || state.data.profiles[0];
      state.selectedProfile = initialProfile && initialProfile.alias;
    },
    setCostAllocations(state, { type, data }) {
      const index = state.data.profiles.findIndex(item => item.type === type);
      state.data.profiles[index].cost_allocations = data;
    },
    setCompany(state, value) {
      state.data.company = {
        ...state.data.company,
        ...value,
      };
    },
    setUserAccount(state, value) {
      state.data.isCompanyAdmin = value.roles.includes('company-admin');
      state.data.uuid = value.uuid;
      state.data.permissions = value.permissions;

      state.data.picture = value.picture;
      state.data.name = `${value.first_name} ${value.last_name}`;
      state.data.firstName = value.first_name;
      state.data.lastName = value.last_name;
      state.data.password = {
        passwordStep1: '',
        passwordStep2: '',
      };
      state.data.defaultCityUuid = value.default_city_uuid;

      // Get the phone!
      const profiles = value.profiles || [];
      const getPhoneByProfile = (profileType) => {
        const profile = profiles.find(p => p.type === profileType);
        if (profile) {
          const notifications = profile.notifications || [];
          const phone = notifications.find(n => n.type === NOTIFICATIONS.PHONE);
          if (phone) {
            state.phone = phone.value;
            return state.phone;
          }
        }
        return undefined;
      };

      state.data.personalPhone = getPhoneByProfile(PROFILES.PERSONAL);
      state.data.phone = getPhoneByProfile(PROFILES.BUSINESS) || state.personalPhone;
      state.data.profiles = value.profiles;

      const company = new Company(value.company);
      Object.assign(state.data.company, company);
    },
    clearPassword(state) {
      state.data.password = DEFAULT_DATA.password;
    },
  },
  actions: {
    async getUserAccount({ commit }) {
      const expiresAtFromLocalStorage = Cookie.get('authData') || localStorage.getItem('authData');
      const authData = expiresAtFromLocalStorage && JSON.parse(expiresAtFromLocalStorage);
      const IdpUuid = authData && authData.idp_uuid;
      commit(MUTATIONS.SET_STATUS, 'LOADING');

      try {
        const data = await backOffice.userCompany.getUserFromIdp(IdpUuid);
        commit(MUTATIONS.SET_USER_ACCOUNT, data);
        commit(MUTATIONS.SET_STATUS, 'LOADED');
        commit(MUTATIONS.SET_INITIAL_SELECTED_PROFILE);
        if (data.roles.includes('company-admin')) {
          commit(
            `${CompanySettingsModule.FULL_NAMESPACE}/SET_FORMDATA`,
            data, {
              root: true,
            },
          );
        }
      } catch (error) {
        commit(MUTATIONS.SET_STATUS, 'ERROR');
        Auth.logout();
      }
    },
  },
};
